import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import LandingPage from "./components/LandingPage";
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import Suggestions from './components/Suggestions';
import ForgetPassword from './components/ForgetPassword';
import UserDetails from './components/UserDetails';
import ChatPage from './components/ChatPage';
import Navbar from './components/Navbar';
import ProfileViews from './components/ProfileViews';
import { useRef, useState } from 'react';
import CallManager from './components/CallManager';
import CallModal from './components/CallModal';
import EditProfile from './components/EditProfile';
import Gifts from './components/Gifts';
import CreateMoment from './components/CreateMoment';
import MomentsFeed from './components/MomentsFeed';
import UserMoments from './components/UserMoments';
import MomentComments from './components/MomentComments';
import GoLivePage from './components/GoLivePage';
import ActiveLivesPage from './components/ActiveLivesPage';
import LiveViewerPage from './components/LiveViewerPage';
import MyEarningsPage from './components/MyEarningsPage';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsers from './components/admin/AdminUsers';
import AdminUserDetail from './components/admin/AdminUserDetail';
import AdminNavbar from "./components/admin/AdminNavbar";
import AdminRoute from './components/admin/AdminRoute';
import AdminReports from './components/admin/AdminReports';
import AdminLive from "./components/admin/AdminLive";
import AdminMoments from "./components/admin/AdminMoments";
import AdminCoins from "./components/admin/AdminCoins";
import AdminAnnouncements from "./components/admin/AdminAnnouncements";
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function SuggestionsWrapper() {
  const location = useLocation();
  const suggestions = location.state?.suggestions || [];
  return <Suggestions suggestions={suggestions} />;
}

function AppWrapper() {
  const location = useLocation();
  const [selectedUser, setSelectedUser] = useState(null);
  const routesWithoutNavbar = [
    "/",
    "/login",
    "/register",
    "/forget",
    "/terms",
    "/privacy"
  ];


  const isAdminRoute =
    location.pathname.startsWith("/admin");

  const showNavbar =
    !isAdminRoute &&
    !routesWithoutNavbar.includes(location.pathname) &&
    !location.pathname.startsWith("/reset-password");


  const callManagerRef = useRef();
  const [callData, setCallData] = useState(null);
  const [isRinging, setIsRinging] = useState(false);
  const [inCall, setInCall] = useState(false);
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [activeCallType, setActiveCallType] = useState(null);
  const [activeCallUser, setActiveCallUser] =
    useState(null);
  const { t } = useTranslation();
  useEffect(() => {

    const handler = (e) => {

      e.preventDefault();

      const installed =
        localStorage.getItem(
          "pwaInstalled"
        ) === "true";

      if (installed) {
        return;
      }


      const dismissedAt =
        localStorage.getItem(
          "pwaPromptDismissedAt"
        );


      if (dismissedAt) {

        const threeDays =
          3 * 24 * 60 * 60 * 1000;

        const elapsed =
          Date.now() -
          Number(dismissedAt);


        if (elapsed < threeDays) {

          setDeferredPrompt(e);

          return;
        }
      }


      setDeferredPrompt(e);


      window.setTimeout(() => {

        setShowInstallPrompt(true);

      }, 12000);
    };


    window.addEventListener(
      "beforeinstallprompt",
      handler
    );


    return () => {

      window.removeEventListener(
        "beforeinstallprompt",
        handler
      );
    };

  }, []);




  const handleInstallClick = async () => {

    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();

    const { outcome } =
      await deferredPrompt.userChoice;

    if (outcome === "accepted") {

      console.log(
        "✅ Installation acceptée"
      );

      localStorage.setItem(
        "pwaInstalled",
        "true"
      );

    } else {

      console.log(
        "❌ Installation refusée"
      );
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleClosePrompt = () => {

    localStorage.setItem(
      "pwaPromptDismissedAt",
      String(Date.now())
    );

    setShowInstallPrompt(false);
  };


  useEffect(() => {

    const handleAppInstalled = () => {

      console.log(
        "✅ MyHalal installed"
      );

      localStorage.setItem(
        "pwaInstalled",
        "true"
      );

      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener(
      "appinstalled",
      handleAppInstalled
    );

    return () => {
      window.removeEventListener(
        "appinstalled",
        handleAppInstalled
      );
    };

  }, []);






  const handleIncomingCall = async (payload) => {
    if (inCall || callData) {
      console.warn(
        "Déjà en appel ou modal déjà affiché."
      );
      return;
    }

    // Display the modal immediately.
    setIsRinging(true);
    setCallData(payload);

    try {
      const storedToken =
        localStorage.getItem('authToken');

      const authorizationHeader =
        storedToken?.startsWith('Bearer ')
          ? storedToken
          : `Bearer ${storedToken || ''}`;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${payload.from}/details`,
        {
          headers: {
            Authorization: authorizationHeader
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Caller request failed: ${response.status}`
        );
      }

      const caller = await response.json();

      setActiveCallUser({
        id: caller.id || payload.from,
        name:
          caller.name ||
          caller.username ||
          `User ${payload.from}`,
        profilePicture:
          caller.mediaUrls?.[0] || null,
        mediaUrls:
          caller.mediaUrls || []
      });

      /*
       * Only update the modal if the same call
       * is still displayed. If the call already ended,
       * callData will be null and the modal stays closed.
       */
      setCallData((currentCall) => {
        if (!currentCall) {
          return null;
        }

        if (
          String(currentCall.from) !==
          String(payload.from)
        ) {
          return currentCall;
        }

        return {
          ...currentCall,
          callerName:
            caller.name ||
            caller.username ||
            `User ${payload.from}`,

          callerImage:
            caller.mediaUrls?.[0] || null
        };
      });

    } catch (error) {
      console.warn(
        '⚠️ Unable to load caller information:',
        error
      );

      setCallData((currentCall) => {
        if (!currentCall) {
          return null;
        }

        return {
          ...currentCall,
          callerName:
            currentCall.callerName ||
            `User ${payload.from}`
        };
      });
    }
  };

  const handleAccept = async () => {
    if (!callData) return;

    const incomingCall = callData;

    try {
      setIsRinging(false);
      setActiveCallType(incomingCall.type);

      setInCall(true);

      if (location.pathname !== "/chat") {
        navigate("/chat");
      }

      // attendre que ChatWindow affiche remoteAudio / remoteVideo
      await new Promise(resolve => setTimeout(resolve, 100));

      await callManagerRef.current?.answerCall(incomingCall);

      setCallData(null);

    } catch (error) {
      console.error("❌ Error accepting call:", error);
      setInCall(false);
      setCallData(null);
    }
  };

  const handleReject = async () => {
    if (!callData) return;

    const rejectedCall = callData;

    setIsRinging(false);
    setCallData(null);
    setInCall(false);
    setActiveCallType(null);
    setActiveCallUser(null);

    try {
      await callManagerRef.current?.rejectCall(
        rejectedCall
      );
    } catch (error) {
      console.error(
        "❌ Error rejecting call:",
        error
      );
    }
  };

  const handleRemoteCallEnded = () => {

    console.log("📴 L'autre utilisateur a terminé l'appel");

    setInCall(false);
    setIsRinging(false);
    setCallData(null);
    setActiveCallType(null);
    setActiveCallUser(null);

  };


  return (
    <>
      {isAdminRoute ? (
        <AdminNavbar />
      ) : (
        showNavbar && <Navbar />
      )}
      <CallManager
        ref={callManagerRef}
        currentUser={JSON.parse(localStorage.getItem('currentUser'))}
        selectedUser={selectedUser}
        onIncomingCall={handleIncomingCall}
        onCallEnded={handleRemoteCallEnded}
        onActiveCallUserChange={
          setActiveCallUser
        }

      />



      {callData && (
        <CallModal
          isOpen={true}
          callData={callData}
          isRinging={isRinging}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}
      {showInstallPrompt && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            bottom: "120px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 32px)",
            maxWidth: "340px",
            background: "#fff",
            padding: "18px",
            borderRadius: "18px",
            boxShadow: "0 12px 35px rgba(0, 0, 0, 0.18)",
            zIndex: 9999,
            textAlign: "center"
          }}
        >
          <p style={{ marginBottom: "12px", fontWeight: "bold" }}>
            💡 {t('pwa.installQuestionBefore', 'Installer')}{' '}
            <span style={{ color: '#f75d9d' }}>MyHalal</span>{' '}
            {t('pwa.installQuestionAfter', 'sur votre appareil ?')}
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              width: '100%'
            }}
          >
            <button
              type="button"
              onClick={handleInstallClick}
              style={{
                flex: 1,
                minHeight: '46px',
                padding: '10px 14px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '7px',
                boxShadow: '0 6px 16px rgba(34, 197, 94, 0.25)'
              }}
            >
              ✅ {t('pwa.install', 'Installer')}
            </button>

            <button
              type="button"
              onClick={handleClosePrompt}
              style={{
                flex: 1,
                minHeight: '46px',
                padding: '10px 14px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '7px',
                boxShadow: '0 6px 16px rgba(239, 68, 68, 0.25)'
              }}
            >
              ❌ {t('pwa.close', 'Fermer')}
            </button>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/forget" element={<ForgetPassword />} />
        <Route path="/gifts" element={<Gifts />} />
        <Route path="/mon-profil" element={<EditProfile />} />
        <Route path="/profile-views" element={<ProfileViews />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/suggestions" element={<SuggestionsWrapper />} />
        <Route path="/user-details/:id" element={<UserDetails />} />

        <Route element={<AdminRoute />}>

          <Route
            path="/admin/reports"
            element={<AdminReports />}
          />
          <Route
            path="/admin/users/:id"
            element={<AdminUserDetail />}
          />

          <Route
            path="/admin/users"
            element={<AdminUsers />}
          />
          <Route
            path="/admin/live"
            element={<AdminLive />}
          />
          <Route
            path="/admin/moments"
            element={<AdminMoments />}
          />

          <Route
            path="/admin/coins"
            element={<AdminCoins />}
          />

          <Route
            path="/admin/announcements"
            element={
              <AdminAnnouncements />
            }
          />
          <Route
            path="/admin"
            element={<AdminDashboard />}
          />

        </Route>
        <Route
          path="/live"
          element={<ActiveLivesPage />}
        />

        <Route
          path="/live/:liveId"
          element={<LiveViewerPage />}
        />

        <Route
          path="/live/start"
          element={<GoLivePage />}
        />
        <Route
          path="/moments/create"
          element={<CreateMoment />}
        />
        <Route
          path="/moments"
          element={<MomentsFeed />}
        />
        <Route
          path="/moments/user/:userId"
          element={<UserMoments />}
        />
        <Route
          path="/moments/:momentId/comments"
          element={<MomentComments />}
        />

        <Route
          path="/earnings"
          element={<MyEarningsPage />}
        />
        <Route
          path="/chat"
          element={
            <ChatPage
              callManagerRef={callManagerRef}
              onIncomingCall={handleIncomingCall}
              inCall={inCall}
              setInCall={setInCall}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              activeCallType={activeCallType}
              setActiveCallType={setActiveCallType}

              activeCallUser={activeCallUser}
              setActiveCallUser={setActiveCallUser}
            />
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppWrapper />
    </BrowserRouter>
  );
}

export default App;
